import { PRICING } from '@/constants/pricing';

/**
 * 주문 금액 계산 결과
 */
export interface OrderTotalResult {
  subtotal: number;
  tax: number;
  total: number;
}

/**
 * 주문 총 금액 계산
 *
 * 소계와 세금을 계산하여 최종 결제 금액을 반환합니다.
 *
 * @param {number} subtotal - 소계 (상품 금액 합계)
 * @param {number} taxRate - 세금 비율 (기본값: PRICING.DEFAULT_TAX_RATE)
 * @returns {OrderTotalResult} 금액 계산 결과
 *
 * @example
 * const { subtotal, tax, total } = calculateOrderTotal(100);
 * // { subtotal: 100, tax: 16, total: 116 }
 */
export const calculateOrderTotal = (
  subtotal: number,
  taxRate: number = PRICING.DEFAULT_TAX_RATE
): OrderTotalResult => {
  const tax = subtotal * taxRate;
  const total = subtotal + tax;

  return {
    subtotal,
    tax,
    total,
  };
};

/**
 * 세금 계산
 *
 * @param {number} amount - 금액
 * @param {number} taxRate - 세금 비율 (기본값: PRICING.DEFAULT_TAX_RATE)
 * @returns {number} 세금
 *
 * @example
 * const tax = calculateTax(100);  // 16
 * const tax = calculateTax(100, 0.08);  // 8
 *
 * @note 백엔드 API는 세율을 퍼센트(16)로 받지만, 프론트엔드는 비율(0.16)로 계산합니다.
 */
export const calculateTax = (
  amount: number,
  taxRate: number = PRICING.DEFAULT_TAX_RATE
): number => {
  return amount * taxRate;
};

/**
 * GROSS 에서 NET 역산 (#299)
 *
 * 레거시 주문/폴백 경로에서 `netSubtotal` 이 누락된 경우, GROSS(IVA 포함)와
 * 별도 저장된 `tax` 로 NET 을 역산한다. `tax` 가 없으면 GROSS 를 그대로
 * 반환하고 호출부에서 로깅해야 한다 — 0 반환은 영수증에서 IVA 100%
 * 왜곡을 유발하므로 피한다.
 *
 * `netSubtotal ?? grossSubtotal` 식 폴백은 NET 자리에 GROSS 값을 넣어
 * "NET + IVA = Total" 불변식을 깨므로 사용하지 말 것. 본 헬퍼로 대체.
 */
export const deriveNetSubtotal = (
  grossSubtotal: number,
  tax: number | undefined | null
): number => {
  if (typeof tax === 'number' && tax > 0) {
    return Math.max(grossSubtotal - tax, 0);
  }
  return grossSubtotal;
};
